import React from 'react';
import {
  Info,
  X,
  Settings2,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';

// Smart Assignment rule explanations. Mirrors the copy used in
// SmartAssignmentPanel so admins see the same rule documentation
// regardless of where they encounter the override badge — settings,
// or the in-flow CreateBookingModal preferences step.
export type ExplanationKey =
  | 'equal_rotation_customer_preference'
  | 'equal_rotation_proximity_override'
  | 'territory_customer_preference'
  | 'territory_proximity_override';

interface ExplanationContent {
  title: string;
  intro: string;
  scenario: {
    setup: string;
    problem: string;
    result: string;
  };
}

export const SMART_ASSIGNMENT_EXPLANATIONS: Record<ExplanationKey, ExplanationContent> = {
  equal_rotation_customer_preference: {
    title: 'Smart Override',
    intro:
      "When this rule is on, all available teammates are scored for every slot type if the next teammate up can't meet the customer's preference or has no availability. The best result per slot type is used, and the skipped teammate stays next in rotation.",
    scenario: {
      setup:
        'Your team is on equal rotation, so bookings cycle evenly between teammates.',
      problem:
        "A customer requests a specific date or time of day, but the next teammate up in the rotation isn't available then.",
      result:
        "The system scores all available teammates and picks the best result for each slot type (company, customer preference, and proximity). The teammate who was skipped keeps their place — they're still next on the next booking.",
    },
  },
  equal_rotation_proximity_override: {
    title: 'Proximity Override',
    intro:
      "When this rule is on, a new appointment that's close to another teammate's existing appointment is routed to that teammate instead — reducing drive time. The teammate who was skipped keeps their place in line, so they're still next on the next booking.",
    scenario: {
      setup:
        'Steve Jamison books an appointment. Joe is next up in the rotation, but his nearest existing appointment is 21.3 miles from Steve. Sam has one 9.3 miles away. The threshold is set to 15 miles.',
      problem:
        'Sending the booking to Joe means a long drive, but sending it to Sam would normally jump the rotation order.',
      result:
        "Sam gets Steve's booking because 9.3 mi is under the 15 mi threshold — and Joe stays next in rotation. He didn't lose his turn; he was just overridden this once. The very next booking still goes to Joe.",
    },
  },
  territory_customer_preference: {
    title: 'Smart Override',
    intro:
      "When this rule is on, all available teammates are scored for every slot type if the territory's assigned teammate can't meet the customer's preference or has no availability. The best result per slot type is used.",
    scenario: {
      setup: 'Each teammate is assigned to specific territories.',
      problem:
        "A customer requests a specific date or time of day, but the territory's normally-assigned teammate isn't available then.",
      result:
        "The system scores all available teammates and picks the best result for each slot type (company, customer preference, and proximity) — keeping the customer's preferred date bookable instead of losing the booking.",
    },
  },
  territory_proximity_override: {
    title: 'Proximity Override',
    intro:
      "When this rule is on, a new appointment that's close to another teammate's existing appointment is routed to that teammate even if the territory points to someone else — reducing drive time without losing the booking.",
    scenario: {
      setup:
        "A new booking lands in a territory normally handled by Joe, but Sam has an existing appointment only a few miles from the new address. The threshold is 15 miles.",
      problem:
        'Sending the job to Joe per territory means a longer drive, while Sam is already nearby.',
      result:
        'Sam gets the booking because the proximity threshold is met. The territory routing is overridden for this booking only — future bookings in the territory still default to Joe.',
    },
  },
};

interface SmartAssignmentExplanationModalProps {
  explanationKey: ExplanationKey | null;
  onClose: () => void;
}

export const SmartAssignmentExplanationModal: React.FC<SmartAssignmentExplanationModalProps> = ({
  explanationKey,
  onClose,
}) => {
  if (!explanationKey) return null;
  const content = SMART_ASSIGNMENT_EXPLANATIONS[explanationKey];

  const scenarioSteps: Array<{
    label: string;
    text: string;
    Icon: typeof Settings2;
    accent: 'slate' | 'amber' | 'emerald';
  }> = [
    { label: 'Setup', text: content.scenario.setup, Icon: Settings2, accent: 'slate' },
    { label: 'Problem', text: content.scenario.problem, Icon: AlertTriangle, accent: 'amber' },
    { label: 'Result', text: content.scenario.result, Icon: CheckCircle2, accent: 'emerald' },
  ];

  const accentClasses = {
    slate: {
      card: 'bg-ink-50 border-ink-200',
      iconWrap: 'bg-surface border border-ink-200',
      icon: 'text-ink-600',
      label: 'text-ink-600',
    },
    amber: {
      card: 'bg-amber-50 border-amber-200',
      iconWrap: 'bg-surface border border-amber-200',
      icon: 'text-amber-600',
      label: 'text-amber-700',
    },
    emerald: {
      card: 'bg-emerald-50 border-emerald-200',
      iconWrap: 'bg-surface border border-emerald-200',
      icon: 'text-emerald-600',
      label: 'text-emerald-700',
    },
  } as const;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-surface rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="px-5 pt-5 pb-4 border-b border-ink-100 bg-gradient-to-b from-blue-50/60 to-white">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="flex-shrink-0 p-2 bg-blue-100 rounded-lg ring-1 ring-blue-200/60">
                <Info className="h-4 w-4 text-blue-600" />
              </div>
              <h3 className="text-15 font-semibold text-ink-900 leading-tight">
                {content.title}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 p-1.5 text-ink-400 hover:text-ink-700 hover:bg-ink-100 rounded-lg transition-colors"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="px-5 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          <p className="text-sm text-ink-700 leading-relaxed">{content.intro}</p>

          <div className="space-y-2.5">
            {scenarioSteps.map(({ label, text, Icon, accent }) => {
              const c = accentClasses[accent];
              return (
                <div
                  key={label}
                  className={`flex gap-3 p-3 border rounded-xl ${c.card}`}
                >
                  <div
                    className={`flex-shrink-0 w-8 h-8 rounded-full ${c.iconWrap} flex items-center justify-center`}
                  >
                    <Icon className={`h-4 w-4 ${c.icon}`} />
                  </div>
                  <div className="flex-1 min-w-0 pt-0.5">
                    <div
                      className={`text-[10.5px] font-bold uppercase tracking-wider mb-1 ${c.label}`}
                    >
                      {label}
                    </div>
                    <p className="text-[13.5px] text-ink-800 leading-relaxed">{text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="px-5 py-3 border-t border-ink-100 bg-ink-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-white bg-ink-900 rounded-lg hover:bg-ink-800 transition-colors shadow-sm"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};
